import { useState, useCallback, useRef, useEffect, useMemo } from 'react';
import { supabase } from '../lib/supabase';
import { parseKickoffTime, resultsPollAction } from '../utils/resultsPolling';

const POLL_INTERVAL_MS = 2 * 60 * 1000;

/**
 * Hook to fetch match results from Supabase cache.
 *
 * The Supabase match_results table is populated by:
 * 1. FootyStats cron (every 30 min) — bulk results for the past 7 days
 * 2. live-score-api.com cron (every 1 min during match hours) — real-time scores
 *
 * Fetches results for matches that have started (kickoff has passed) and aren't
 * already FINISHED. Polls every 2 minutes while any kicked-off match is still
 * unresolved (not FINISHED/POSTPONED/CANCELLED), and keeps the timer alive for
 * matches that have not kicked off yet so a page opened pre-kickoff updates.
 * Detects goal changes between polls and sets a goalScoredSide flag for animations.
 */
function useResults() {
  const [results, setResults] = useState({});
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const pollIntervalRef = useRef(null);
  const matchesRef = useRef(null);
  const goalTimersRef = useRef({});
  const resultsRef = useRef({});
  const fetchRef = useRef(null);

  useEffect(() => {
    resultsRef.current = results;
  }, [results]);

  const stopPolling = useCallback(() => {
    if (pollIntervalRef.current) {
      clearInterval(pollIntervalRef.current);
      pollIntervalRef.current = null;
    }
  }, []);

  // One poll tick: fetch if something kicked off and is unresolved, idle while
  // waiting for a kickoff, stop once everything relevant is resolved.
  const pollTick = useCallback(() => {
    const matches = matchesRef.current;
    const action = resultsPollAction(matches, resultsRef.current, Date.now());
    if (action === 'stop') {
      console.log('All tracked matches resolved — stopping polling');
      stopPolling();
    } else if (action === 'fetch' && fetchRef.current) {
      fetchRef.current(matches);
    }
  }, [stopPolling]);

  const fetchResults = useCallback(async (matches) => {
    if (!matches || matches.length === 0) return;

    // Store matches ref for polling
    matchesRef.current = matches;

    if (!pollIntervalRef.current
        && resultsPollAction(matches, resultsRef.current, Date.now()) !== 'stop') {
      pollIntervalRef.current = setInterval(pollTick, POLL_INTERVAL_MS);
    }

    const now = Date.now();

    // Filter to matches that need results:
    // - Already FINISHED: skip (no update needed)
    // - Kickoff hasn't passed yet: skip
    // - Everything else (SCHEDULED, IN_PLAY, or no status): fetch if kickoff has passed
    const matchesNeedingResults = matches.filter(m => {
      const status = m.result?.status;
      if (status === 'FINISHED') return false;

      const kickoffTime = parseKickoffTime(m.date_time);
      if (!kickoffTime) return false;

      // Match has started (kickoff time has passed)
      return kickoffTime < now;
    });

    if (matchesNeedingResults.length === 0) {
      console.log('No matches need result fetching');
      return;
    }

    console.log(`Fetching results for ${matchesNeedingResults.length} matches from Supabase cache`);

    setLoading(true);
    setError(null);

    try {
      // Collect unique dates from matches needing results
      const dates = new Set();
      matchesNeedingResults.forEach(m => {
        const d = parseMatchDate(m.date_time);
        if (d) dates.add(d);
      });

      const dateArray = Array.from(dates);
      if (dateArray.length === 0) {
        setLoading(false);
        return;
      }

      // Single query to Supabase — fetches all results for relevant dates
      const { data: dbResults, error: dbError } = await supabase
        .from('match_results')
        .select('footystats_id, match_date, home_team, away_team, home_goals, away_goals, ht_home_goals, ht_away_goals, status, elapsed_minutes, match_time')
        .in('match_date', dateArray);

      if (dbError) {
        console.warn('Supabase query error:', dbError.message);
        setError(dbError.message);
        setLoading(false);
        return;
      }

      if (!dbResults || dbResults.length === 0) {
        console.log('No results found in Supabase cache yet');
        setLoading(false);
        return;
      }

      // Group database results by date for faster lookups
      const resultsByDate = {};
      for (const r of dbResults) {
        if (!resultsByDate[r.match_date]) {
          resultsByDate[r.match_date] = [];
        }
        resultsByDate[r.match_date].push(r);
      }

      // Match predictions with database results using fuzzy team matching
      const allResults = {};
      for (const match of matchesNeedingResults) {
        const dateStr = parseMatchDate(match.date_time);
        if (!dateStr) continue;

        const dayResults = resultsByDate[dateStr] || [];

        for (const dbMatch of dayResults) {
          if (
            teamsMatch(match.home_team, dbMatch.home_team) &&
            teamsMatch(match.away_team, dbMatch.away_team)
          ) {
            allResults[match.id] = {
              status: dbMatch.status,
              homeGoals: dbMatch.home_goals,
              awayGoals: dbMatch.away_goals,
              htHomeGoals: dbMatch.ht_home_goals,
              htAwayGoals: dbMatch.ht_away_goals,
              footystatsId: dbMatch.footystats_id,
              elapsedMinutes: dbMatch.elapsed_minutes,
              matchTime: dbMatch.match_time,
            };
            break;
          }
        }
      }

      console.log(`Matched ${Object.keys(allResults).length} results from Supabase cache`);

      // Merge with existing results — detect goals, keep FINISHED results, update others
      setResults(prev => {
        const merged = { ...prev };
        for (const [id, result] of Object.entries(allResults)) {
          const old = prev[id];
          // Detect goal changes between polls
          if (old && result.homeGoals != null && result.awayGoals != null) {
            if (old.homeGoals != null && old.awayGoals != null) {
              if (result.homeGoals > old.homeGoals) {
                result.goalScoredSide = 'home';
              } else if (result.awayGoals > old.awayGoals) {
                result.goalScoredSide = 'away';
              }
            }
          }
          merged[id] = result;

          // Clear goalScoredSide after 8 seconds
          if (result.goalScoredSide) {
            // Clear any existing timer for this match
            if (goalTimersRef.current[id]) {
              clearTimeout(goalTimersRef.current[id]);
            }
            goalTimersRef.current[id] = setTimeout(() => {
              setResults(current => {
                if (current[id]?.goalScoredSide) {
                  const updated = { ...current };
                  updated[id] = { ...updated[id] };
                  delete updated[id].goalScoredSide;
                  return updated;
                }
                return current;
              });
              delete goalTimersRef.current[id];
            }, 60000);
          }
        }
        return merged;
      });
    } catch (err) {
      console.error('Error fetching results from Supabase:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [pollTick]);

  useEffect(() => {
    fetchRef.current = fetchResults;
  }, [fetchResults]);

  // Detect live matches (exposed for the UI)
  const hasLiveMatches = useMemo(() => {
    return Object.values(results).some(r => r.status === 'IN_PLAY' || r.status === 'HALF_TIME');
  }, [results]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      stopPolling();
      // Clear all goal highlight timers
      for (const timerId of Object.values(goalTimersRef.current)) {
        clearTimeout(timerId);
      }
      goalTimersRef.current = {};
    };
  }, [stopPolling]);

  const refreshResults = useCallback((matches) => {
    fetchResults(matches);
  }, [fetchResults]);

  return {
    results,
    resultsLoading: loading,
    resultsError: error,
    hasLiveMatches,
    fetchResults,
    refreshResults,
  };
}

// ─── Team matching utilities ───────────────────────────────────────

function normalizeTeam(name) {
  if (!name) return '';
  let normalized = name.toLowerCase();

  const terms = [
    'fc', 'afc', 'sc', 'ac', 'as', 'ss', 'cf', 'cd', 'ud', 'rc',
    'kv', 'sk', 'fk', 'nk', 'bk', 'if', 'bv', 'sv', 'vfb', 'tsv',
    'united', 'city', 'town', 'wanderers', 'rovers', 'athletic',
    'albion', 'hotspur', 'wednesday', 'argyle', 'county',
  ];

  for (const term of terms) {
    normalized = normalized.replace(new RegExp(`\\b${term}\\b`, 'gi'), '');
  }

  normalized = normalized.replace(/[^a-z0-9\s]/g, '');
  normalized = normalized.replace(/\s+/g, ' ').trim();

  return normalized;
}

function similarity(a, b) {
  if (a.length === 0) return b.length === 0 ? 1 : 0;
  if (b.length === 0) return 0;

  const matrix = [];
  for (let i = 0; i <= b.length; i++) {
    matrix[i] = [i];
  }
  for (let j = 0; j <= a.length; j++) {
    matrix[0][j] = j;
  }

  for (let i = 1; i <= b.length; i++) {
    for (let j = 1; j <= a.length; j++) {
      if (b.charAt(i - 1) === a.charAt(j - 1)) {
        matrix[i][j] = matrix[i - 1][j - 1];
      } else {
        matrix[i][j] = Math.min(
          matrix[i - 1][j - 1] + 1,
          matrix[i][j - 1] + 1,
          matrix[i - 1][j] + 1
        );
      }
    }
  }

  const maxLen = Math.max(a.length, b.length);
  return (maxLen - matrix[b.length][a.length]) / maxLen;
}

function teamsMatch(team1, team2) {
  const n1 = normalizeTeam(team1);
  const n2 = normalizeTeam(team2);

  if (!n1 || !n2) return false;
  if (n1 === n2) return true;

  const sim = similarity(n1, n2);
  if (sim >= 0.6) return true;

  if (n1.length >= 4 && n2.length >= 4) {
    if (n1.includes(n2) || n2.includes(n1)) return true;
  }

  const words1 = n1.split(/\s+/).filter(w => w.length >= 3);
  const words2 = n2.split(/\s+/).filter(w => w.length >= 3);
  const overlap = words1.filter(w => words2.includes(w));
  if (overlap.some(w => w.length >= 4)) return true;

  return false;
}

// ─── Date parsing utilities ────────────────────────────────────────

/**
 * Parse date string to YYYY-MM-DD format for Supabase query
 * Handles format like "Jan 20 2026 - 8:00pm"
 */
function parseMatchDate(dateStr) {
  if (!dateStr || dateStr === 'TBD') return null;

  try {
    if (dateStr.includes(' - ')) {
      const [datePart, timePart] = dateStr.split(' - ');
      const timeMatch = timePart.match(/(\d{1,2}):(\d{2})(am|pm)/i);

      if (timeMatch) {
        let hours = parseInt(timeMatch[1]);
        const period = timeMatch[3].toLowerCase();
        if (period === 'pm' && hours !== 12) hours += 12;
        if (period === 'am' && hours === 12) hours = 0;

        const date = new Date(datePart + ' ' + hours + ':00 GMT');
        if (!isNaN(date.getTime())) {
          return date.toISOString().split('T')[0];
        }
      }
    }

    const date = new Date(dateStr);
    if (!isNaN(date.getTime())) {
      return date.toISOString().split('T')[0];
    }
  } catch {
    // ignore parse errors
  }
  return null;
}

export default useResults;
